import React, { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import { Route, Switch, useHistory } from 'react-router';
import { setFocusVisibility, Spinner, SpinnerSize } from '@fluentui/react';
import { authlog } from 'AuthLogger';
import { useAuth } from 'AuthProvider';
import ClarityNoticeBanner from 'ClarityNoticeBanner';
import { pageDataService } from 'common';
import { Layout } from 'common/components/Layout';
import { GlobalToasts } from 'common/components/Notifications';
import useHandleAuthError from 'common/hooks/useHandleAuthError';
import { useNavigation } from 'common/hooks/useNavigation';
import usePlatform from 'common/hooks/usePlatform';
import { useScreen } from 'common/hooks/useScreen';
import { isDiagnosticsPageEnabled, isLicenseRequired } from 'common/utilities/FlightSetting';
import { EventType, IRouteChangeEventDetail } from 'common/utilities/PerformanceService.types';
import { ignoreCaseIncludes, localeIgnoreCaseEquals } from 'common/utilities/String';
import { WaitForInitialization } from 'common/WaitForInitialization';
import { ActiveSessionProvider } from 'features/creditMonitoring/ActiveSessionProvider';
import { IdleDetectionProvider } from 'features/creditMonitoring/IdleDetectionProvider';
import { oneTimeScanPath, routes } from 'Routes';
import { hideNavigation, isApiCallComplete, screenSizeChanged, useAppDispatch, useAppSelector } from 'store';
import { useUserProfile } from 'store/userProfile/useUserProfile';
import { useWebView } from 'store/webView/useWebView';

import initializeWebView from './nativeAppShim';

const Loading = () => <Spinner size={SpinnerSize.large} style={{ flex: 1 }} />;

const Paywall = lazy(() => import('./Paywall/Paywall'));
const DiagnosticsSettings = lazy(() => import('./common/utilities/DiagnosticsPage/DiagnosticsSettings'));
const ErrorPage404 = lazy(() => import('./common/components/Errors/ErrorPage404'));

export const AuthenticatedApp = (): JSX.Element => {
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { license } = useAuth();
  const { isWebView, webViewPlatform } = useWebView();
  const { isMobileScreen } = useScreen();
  const { isIOSDevice } = usePlatform();

  const [renderPaywall, setRenderPaywall] = useState(false);
  const { userProfileLoadingStatus } = useUserProfile();
  const hasActiveOneTimeScanResult = pageDataService.hasActiveOneTimeScanResult();
  const originUrl = useAppSelector((state) => state.auth.requestState.originUrl);
  const { navigateTo } = useNavigation();

  const onResize = useCallback((): void => {
    dispatch(screenSizeChanged());
  }, [dispatch]);

  useHandleAuthError();

  useEffect(() => {
    window.addEventListener('resize', onResize);
    history.listen(() => {
      const eventDetail: IRouteChangeEventDetail = {
        startTime: Date.now(),
      };
      dispatch(hideNavigation(false));
      const event = new CustomEvent(EventType.RouteChange, { cancelable: true, detail: { ...eventDetail } });
      document.body.dispatchEvent(event);
    });
  }, [dispatch, history, onResize]);

  useEffect(() => {
    // Must show focus outline on iOS in case someone is using an external keyboard
    // and has "full keyboard access" turned ON in accessibility settings.
    // When these settings are enabled, iOS does not surface a keydown/keyup event for the "tab" key.
    // As a result, FluentUI doesn't know when a keyboard is being used and does not turn on focus outline visibility
    // by adding the "ms-Fabric--isFocusVisible" class to the document body.
    // This is a workaround to ensure that focus is always visible on iOS, whether in
    // webview or in a mobile browser.
    if (isIOSDevice) {
      setFocusVisibility(true);
    }
    if (isWebView) {
      initializeWebView();
    }
  }, [isIOSDevice, isMobileScreen, isWebView, webViewPlatform]);

  const shouldCheckLicense =
    !isWebView && // no license check required for webview
    isLicenseRequired(); // whether license should be checked

  useEffect(() => {
    // Render paywall if no active license
    if (shouldCheckLicense && license && !license.isSubscriptionActive) {
      // when license is active after purchase, paywall will render "Thanks for subscribing" message, so we want to keep rendering paywall page
      setRenderPaywall(true);
    }
  }, [license, shouldCheckLicense]);

  if (localeIgnoreCaseEquals(window.location.pathname, '/signin-spa')) {
    if (originUrl) {
      // client-side redirection to origin once login is successful
      authlog(`AuthenticatedApp: client-side redirection to origin: ${originUrl}`);
      navigateTo(originUrl);
      // replace the current origin (/signin-spa) with the original request url
      pageDataService.resetOrigin();
    } else {
      // empty login redirect page
      return <Loading />;
    }
  }

  if (isDiagnosticsPageEnabled() && localeIgnoreCaseEquals(window.location.pathname, '/_diagnostics')) {
    return (
      <Suspense fallback={<Loading />}>
        <Route component={DiagnosticsSettings} />
      </Suspense>
    );
  }

  const isSandboxRoute = ignoreCaseIncludes(window.location.pathname, '/sandbox');
  const isOTSRoute = localeIgnoreCaseEquals(window.location.pathname, oneTimeScanPath);

  // Redirect is only for unlicensed users who have an active scan to view:
  const shouldRedirectToOTS =
    !isSandboxRoute &&
    !isOTSRoute &&
    // if true: (1) user doesn't have a license, and (2) user has an active scan
    hasActiveOneTimeScanResult;

  if (shouldRedirectToOTS) {
    window.location.href = oneTimeScanPath;
    return <Loading />;
  }

  // License check
  if (
    shouldCheckLicense &&
    !isSandboxRoute &&
    // OTS route should never show a paywall.
    !isOTSRoute
  ) {
    if (!license || !isApiCallComplete(userProfileLoadingStatus)) {
      // license info and user profile still being fetched
      return <Loading />;
    }

    // check license.isSubscriptionActive as well here to prevent flickering (when renderPaywall state is being updated)
    if (renderPaywall || (license && !license.isSubscriptionActive)) {
      return (
        <Suspense fallback={<Loading />}>
          <Route component={Paywall} />
        </Suspense>
      );
    }
  }

  return (
    <WaitForInitialization fallback={<Loading />}>
      <IdleDetectionProvider>
        <ActiveSessionProvider>
          <Switch>
            <Route key="allRoutes" path="/">
              <Layout>
                <Suspense fallback={<Loading />}>
                  <Switch>
                    {routes.map(({ key, exact, path, component: Component }) => (
                      <Route key={key} exact={exact} path={path} component={Component} />
                    ))}

                    <Route component={ErrorPage404} />
                  </Switch>
                </Suspense>
                <GlobalToasts />
                <ClarityNoticeBanner />
              </Layout>
            </Route>
          </Switch>
        </ActiveSessionProvider>
      </IdleDetectionProvider>
    </WaitForInitialization>
  );
};

export default AuthenticatedApp;
